import { ReactNode } from 'react';
import { Box, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ReactComponent as BackArrow } from 'assets/BackArrow.svg';
import { ReactComponent as NewSpeedchainLogo } from 'assets/new_logo.svg';
import { useVisualViewportHeight } from 'app/pages/mobile/hooks/useVisualViewportHeight';
import { NATIVE_SAFE_TOP } from 'app/pages/mobile/utils/constants';

const Container = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  backgroundColor: 'white',
  overflowY: 'auto',
});

export const maxWidth = 400;

interface MobileAuthLayoutProps {
  onBack?: () => void;
  children: ReactNode;
  footer?: ReactNode;
}

function MobileAuthLayout({ onBack, children, footer }: MobileAuthLayoutProps): JSX.Element {
  // Bind the container height to the visual viewport so, when the keyboard
  // opens, the container shrinks and the bottom footer (Sign In button) floats
  // up above the keyboard instead of hiding behind it. Falls back to 100dvh
  // (then 100vh) where visualViewport is unavailable.
  const viewportHeight = useVisualViewportHeight();
  return (
    <Container
      sx={{
        p: 2,
        // SEE-1715: #root is already padded below the status bar.
        height: viewportHeight
          ? `calc(${viewportHeight}px - ${NATIVE_SAFE_TOP})`
          : [`calc(100vh - ${NATIVE_SAFE_TOP})`, `calc(100dvh - ${NATIVE_SAFE_TOP})`],
      }}
    >
      <Box>
        {/* Header with back arrow and logo */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr auto',
            alignItems: 'center',
            mb: 2,
            gap: 2,
          }}
        >
          <IconButton
            onClick={onBack}
            aria-label="Back"
            sx={{ color: 'primary.main', width: 40, height: 40, visibility: onBack ? 'visible' : 'hidden' }}
          >
            <BackArrow style={{ height: 24, width: 24 }} />
          </IconButton>

          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <NewSpeedchainLogo style={{ width: 178, height: 28 }} />
          </Box>

          {/* Ghost button to ensure perfect centering */}
          <IconButton disabled sx={{ width: 40, height: 40, visibility: 'hidden' }}>
            <BackArrow style={{ height: 24, width: 24 }} />
          </IconButton>
        </Box>

        {/* Main content */}
        <Box
          sx={{
            width: '100%',
            maxWidth,
            mx: 'auto',
          }}
        >
          {children}
        </Box>
      </Box>

      {/* Footer content */}
      {footer && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 2,
            mb: 4,
            mx: 'auto',
            maxWidth,
            width: '100%',
          }}
        >
          {footer}
        </Box>
      )}
    </Container>
  );
}

export default MobileAuthLayout;
